import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Modal, Popconfirm, Spin, Switch, Table, Tag } from "antd";
import { toast } from "react-toastify";
import PermissionGate from "../commoncomponent/PermissionGate";
import {
  getScreenMaintenanceConfig,
  upsertScreenMaintenanceConfig,
  getScreenMaintenanceHistory,
} from "../../../service/api";

const PLATFORM_LABELS = { android: "Android", ios: "iOS" };

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "—");

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
};

const CHANGE_TAG = {
  TURNED_ON: { color: "red", label: "Turned ON" },
  TURNED_OFF: { color: "green", label: "Turned OFF" },
  UPDATED: { color: "default", label: "Updated" },
};

const HISTORY_COLUMNS = [
  { title: "When", dataIndex: "createdAt", render: fmtDateTime },
  { title: "Screen", dataIndex: "screenKey", render: (v) => <Tag>{v}</Tag> },
  {
    title: "Platform",
    dataIndex: "platform",
    render: (v) => <Tag>{PLATFORM_LABELS[v] || v}</Tag>,
  },
  {
    title: "Change",
    dataIndex: "changeType",
    render: (v) => {
      const t = CHANGE_TAG[v] || CHANGE_TAG.UPDATED;
      return <Tag color={t.color}>{t.label}</Tag>;
    },
  },
  { title: "ETA", dataIndex: "estimatedEndTime", render: fmtDateTime },
  { title: "By", dataIndex: "updatedByEmail", render: (v) => v || "—" },
];

// Self-contained editor — owns its draft state, so typing never re-renders the
// parent table (no input focus loss). Seeds from `initial` each time it opens.
const ScreenEditModal = ({ open, screen, platform, initial, saving, onCancel, onSave }) => {
  const [draft, setDraft] = useState(initial);

  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  if (!screen) return null;
  const label = PLATFORM_LABELS[platform] || platform;
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  const SaveBtn = (
    <Button type="primary" danger={!!draft.enabled} loading={saving}>
      Save
    </Button>
  );

  return (
    <Modal
      open={open}
      title={`${screen.label} — ${label}`}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <PermissionGate key="save" module="maintenance" action="update">
          {draft.enabled ? (
            <Popconfirm
              title={`Put ${screen.label} under maintenance for ${label}?`}
              description={`${label} users opening this screen will see a full-screen notice and cannot use it until you turn this off. They can still navigate to other screens.`}
              okText="Save"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
              onConfirm={() => onSave(draft)}
            >
              {SaveBtn}
            </Popconfirm>
          ) : (
            <span onClick={() => onSave(draft)}>{SaveBtn}</span>
          )}
        </PermissionGate>,
      ]}
    >
      <div style={{ display: "grid", gap: 16, marginTop: 8 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <label style={{ fontWeight: 600 }}>
            Screen maintenance
            <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 400 }}>
              When on, this screen shows a full-screen notice on {label}.
            </div>
          </label>
          <Switch checked={!!draft.enabled} onChange={(v) => set({ enabled: v })} />
        </div>

        <div>
          <label style={{ fontSize: 12, opacity: 0.7 }}>Title</label>
          <Input
            value={draft.title}
            maxLength={200}
            placeholder="Temporarily Unavailable"
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>

        <div>
          <label style={{ fontSize: 12, opacity: 0.7 }}>Message</label>
          <Input.TextArea
            value={draft.message}
            maxLength={1000}
            rows={3}
            placeholder="Shown to users on the screen maintenance notice"
            onChange={(e) => set({ message: e.target.value })}
          />
        </div>

        <div>
          <label style={{ fontSize: 12, opacity: 0.7 }}>Estimated end time (optional)</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="datetime-local"
              value={toLocalInput(draft.estimatedEndTime)}
              onChange={(e) => set({ estimatedEndTime: e.target.value || null })}
              style={{ padding: "6px 8px", border: "1px solid #d9d9d9", borderRadius: 6 }}
            />
            {draft.estimatedEndTime && (
              <Button size="small" onClick={() => set({ estimatedEndTime: null })}>
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ScreenMaintenanceTab = () => {
  const [loading, setLoading] = useState(false);
  const [screens, setScreens] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState({ open: false, screen: null, platform: null });

  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async (page = 1) => {
    setHistoryLoading(true);
    try {
      const res = await getScreenMaintenanceHistory({ page, limit: 20 });
      if (res?.status) {
        setHistory(res.data?.rows || []);
        setHistoryTotal(res.data?.total || 0);
        setHistoryPage(res.data?.page || page);
      }
    } catch (e) {
      // non-critical
    } finally {
      setHistoryLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await getScreenMaintenanceConfig();
      if (res?.status) {
        setScreens(res.data?.screens || []);
      } else {
        toast.error(res?.message || "Load failed", { toastId: "screen-maint-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Load failed", {
        toastId: "screen-maint-error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHistory(1);
  }, []);

  const openEditor = (screen, platform) =>
    setEditor({ open: true, screen, platform });

  const closeEditor = () => setEditor((e) => ({ ...e, open: false }));

  const editorInitial = editor.screen
    ? editor.screen.platforms[editor.platform] || {
        enabled: false,
        title: "Temporarily Unavailable",
        message: "This section is under maintenance. Please check back shortly.",
        estimatedEndTime: null,
      }
    : {};

  const saveEditor = async (draft) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await upsertScreenMaintenanceConfig({
        screenKey: editor.screen.key,
        platform: editor.platform,
        enabled: !!draft.enabled,
        title: draft.title || "",
        message: draft.message || "",
        estimatedEndTime: draft.estimatedEndTime
          ? new Date(draft.estimatedEndTime).toISOString()
          : null,
      });
      if (res?.status) {
        toast.success(`${editor.screen.label} (${PLATFORM_LABELS[editor.platform]}) saved`);
        // Patch the in-memory grid with the canonical server value.
        setScreens((prev) =>
          prev.map((s) =>
            s.key === editor.screen.key
              ? { ...s, platforms: { ...s.platforms, [editor.platform]: res.data } }
              : s,
          ),
        );
        closeEditor();
        loadHistory(1);
      } else {
        toast.error(res?.message || "Save failed", { toastId: "screen-maint-error" });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Save failed", {
        toastId: "screen-maint-error",
      });
    } finally {
      setSaving(false);
    }
  };

  const platformCell = (screen, platform) => {
    const cfg = screen.platforms?.[platform] || {};
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {cfg.enabled ? <Tag color="red">ON</Tag> : <Tag color="green">Live</Tag>}
        <Button size="small" onClick={() => openEditor(screen, platform)}>
          Edit
        </Button>
      </div>
    );
  };

  const columns = [
    { title: "Screen", dataIndex: "label", render: (v, r) => <b>{v}</b> },
    {
      title: "Android",
      key: "android",
      render: (_, r) => platformCell(r, "android"),
    },
    { title: "iOS", key: "ios", render: (_, r) => platformCell(r, "ios") },
  ];

  return (
    <>
      <p style={{ opacity: 0.7, marginBottom: 16 }}>
        Put an individual screen under maintenance for a specific OS. Unlike
        global maintenance, this only covers that one screen — the user sees a
        full-screen notice there and cannot use it, but can still go back or
        navigate to other screens.
      </p>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16, maxWidth: 750 }}
        message="Screen maintenance is non-blocking for the rest of the app. The app reflects changes within ~30 seconds."
      />

      {loading ? (
        <Spin />
      ) : (
        <div className="card" style={{ padding: 16, background: "white", maxWidth: 760, marginBottom: 24 }}>
          <Table
            rowKey={(r) => r.key}
            size="small"
            columns={columns}
            dataSource={screens}
            pagination={false}
          />
        </div>
      )}

      <div className="card" style={{ padding: 16, background: "white", maxWidth: 980 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <h5 style={{ margin: 0 }}>History</h5>
          <Button size="small" onClick={() => loadHistory(historyPage)} loading={historyLoading}>
            Refresh
          </Button>
        </div>
        <Table
          rowKey={(r) => r._id}
          size="small"
          loading={historyLoading}
          columns={HISTORY_COLUMNS}
          dataSource={history}
          pagination={{
            current: historyPage,
            pageSize: 20,
            total: historyTotal,
            showSizeChanger: false,
            onChange: (p) => loadHistory(p),
          }}
        />
      </div>

      <ScreenEditModal
        open={editor.open}
        screen={editor.screen}
        platform={editor.platform}
        initial={editorInitial}
        saving={saving}
        onCancel={closeEditor}
        onSave={saveEditor}
      />
    </>
  );
};

export default ScreenMaintenanceTab;
